import { useEffect, useState } from "react";
import {
    subscribeToStorageKey,
    watchStorageKey,
} from "../utils/localStorageEmitter";

export const LOCALSTORAGE_TERREIRO_CURRENT =
  "ilovemacumba:terreiro:current" as const;

// Register key for same-window change notifications
watchStorageKey(LOCALSTORAGE_TERREIRO_CURRENT);

export type CachedTerreiro = {
  id?: string;
  [k: string]: unknown;
};

function readTerreiroFromStorage(): CachedTerreiro | null | undefined {
  try {
    if (typeof window === "undefined" || !window.localStorage) return undefined;

    const raw = localStorage.getItem(LOCALSTORAGE_TERREIRO_CURRENT);
    if (!raw) return undefined;

    const parsed = JSON.parse(raw);
    // Handle explicit null (personal profile)
    if (parsed === null) return null;

    return parsed as CachedTerreiro;
  } catch {
    return undefined;
  }
}

/**
 * Read the cached terreiro from LocalStorage.
 * - Reads the global `ilovemacumba:terreiro:current` key.
 * - Returns null when explicitly set to null (personal profile)
 * - Returns undefined when not set or on error
 *
 * This hook is reactive and updates when localStorage changes.
 */
export function useCachedTerreiro() {
  const [terreiro, setTerreiro] = useState<CachedTerreiro | null | undefined>(
    readTerreiroFromStorage,
  );

  useEffect(() => {
    // Event-driven: notified on same-window setItem AND cross-tab StorageEvent
    const unsubscribe = subscribeToStorageKey(
      LOCALSTORAGE_TERREIRO_CURRENT,
      () => setTerreiro(readTerreiroFromStorage()),
    );

    return unsubscribe;
  }, []);

  return terreiro;
}
